import { Card } from "@general-backoffice/core";
import type { User } from "@investhinks/api-client";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card as RTCard, CardBody as RTCardBody, Spinner } from "reactstrap";

import { getCountries } from "../../utils/getCountries";

interface AdminUserOverviewCardProps {
  user?: Partial<User> | null;
  referrer?: Partial<Pick<User, "name" | "email">> | null;
  onNavigateToReferrer?: () => void;
  onDisableVIP?: () => void;
  onManagePartnership?: () => void;
  onToggleManualBilling?: () => void;
  onAssignReferrer?: () => void;
  isLoading?: boolean;
}

const AdminUserOverviewCard = ({
  user,
  referrer,
  onNavigateToReferrer,
  onDisableVIP,
  onManagePartnership,
  onToggleManualBilling,
  onAssignReferrer,
  isLoading,
}: AdminUserOverviewCardProps) => {
  const { t, i18n } = useTranslation("users", { keyPrefix: "adminOverview" });

  // Get country names map for current language
  const countries = useMemo(() => getCountries(i18n.language), [i18n.language]);

  if (isLoading) {
    return (
      <RTCard>
        <Card.Header title={t("title")} />
        <RTCardBody style={styles.loadingBody}>
          <Spinner size="sm" color="secondary" />
        </RTCardBody>
      </RTCard>
    );
  }

  const { name, email, isVIP, isManualPayment, referrerType, subscription_tier, country, createdAt, role, taxProfile } =
    user || {};

  const isPartner = !!referrerType && ["AMBASSADOR", "INFLUENCER"].includes(referrerType);
  const tierName = subscription_tier?.name || subscription_tier?.code;
  // Prefer country from taxProfile, fallback to user.country
  const countryCode = taxProfile?.country || country;
  const displayCountry = countryCode ? countries[countryCode] || countryCode : null;

  return (
    <RTCard>
      <Card.Header title={t("title")} />
      <RTCardBody style={styles.cardBody}>
        {/* Badges Row */}
        <div style={styles.badgesRow}>
          {isVIP && (
            <Badge color="warning" style={styles.badge}>
              VIP
            </Badge>
          )}
          {isPartner && (
            <Badge color="info" style={styles.badge}>
              {referrerType}
            </Badge>
          )}
          {isManualPayment && (
            <Badge color="secondary" style={styles.badge}>
              {t("manualPayment")}
            </Badge>
          )}
          {role?.name === "admin" && (
            <Badge color="danger" style={styles.badge}>
              ADMIN
            </Badge>
          )}
        </div>

        {/* User Info Grid */}
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <span style={styles.label}>{t("nameLabel")}</span>
            <span style={styles.value}>{name || "-"}</span>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.label}>{t("emailLabel")}</span>
            <span style={styles.value}>{email || "-"}</span>
          </div>

          <div style={{ ...styles.infoItem, alignItems: "center" }}>
            <span style={styles.label}>{t("tierLabel")}</span>
            {subscription_tier ? (
              <Badge color={subscription_tier.isDefault ? "warning" : "info"} style={styles.tierBadge}>
                {subscription_tier.isDefault && <i className="fas fa-star mr-1" />}
                {tierName}
              </Badge>
            ) : (
              <span style={styles.value}>{t("noTier")}</span>
            )}
          </div>

          <div style={styles.infoItem}>
            <span style={styles.label}>{t("countryLabel")}</span>
            <span style={styles.value}>{displayCountry || "-"}</span>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.label}>{t("registeredLabel")}</span>
            <span style={styles.value}>{createdAt ? new Date(createdAt).toLocaleDateString() : "-"}</span>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.label}>{t("referredByLabel")}</span>
            {referrer ? (
              <div style={styles.referrerRow}>
                <button
                  type="button"
                  style={{ ...styles.linkButton, ...styles.value, ...styles.clickable }}
                  onClick={onNavigateToReferrer}
                >
                  {referrer.name || referrer.email}
                  <i className="fas fa-external-link-alt" style={styles.linkIcon} aria-hidden="true" />
                </button>
                {onAssignReferrer && (
                  <button
                    type="button"
                    style={{ ...styles.linkButton, ...styles.changeLink }}
                    onClick={onAssignReferrer}
                  >
                    {t("changeReferrer")}
                  </button>
                )}
              </div>
            ) : (
              <div style={styles.referrerRow}>
                <span style={styles.noValue}>-</span>
                {onAssignReferrer && (
                  <button
                    type="button"
                    style={{ ...styles.linkButton, ...styles.changeLink }}
                    onClick={onAssignReferrer}
                  >
                    {t("assignReferrer")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {(onManagePartnership || (isVIP && onDisableVIP) || onToggleManualBilling) && (
          <div style={styles.actionSection}>
            {onManagePartnership && (
              <Button color="primary" outline size="sm" onClick={onManagePartnership}>
                <i className="fas fa-user-tag mr-2" />
                {t("managePartnership")}
              </Button>
            )}
            {onToggleManualBilling && (
              <Button color={isManualPayment ? "success" : "warning"} outline size="sm" onClick={onToggleManualBilling}>
                <i className={`fas fa-${isManualPayment ? "credit-card" : "file-invoice-dollar"} mr-2`} />
                {isManualPayment ? t("enableAutoBilling") : t("enableManualBilling")}
              </Button>
            )}
            {isVIP && onDisableVIP && (
              <Button color="danger" outline size="sm" onClick={onDisableVIP}>
                <i className="fas fa-user-minus mr-2" />
                {t("disableVIP")}
              </Button>
            )}
          </div>
        )}
      </RTCardBody>
    </RTCard>
  );
};

const styles: Record<string, React.CSSProperties> = {
  // Strips the browser's button chrome so these read as the inline links they
  // replaced. They are <button> elements for keyboard and screen-reader
  // access, not for appearance. This key was referenced before it existed,
  // and because `styles` is a loose Record that slipped past the typecheck,
  // leaving "Assign Referrer" as a default grey box that matched nothing.
  // `outline` is deliberately left alone so focus stays visible.
  linkButton: {
    appearance: "none",
    WebkitAppearance: "none",
    border: "none",
    borderRadius: 0,
    background: "transparent",
    padding: 0,
    margin: 0,
    font: "inherit",
    lineHeight: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
  cardBody: {
    padding: "16px 20px",
  },
  loadingBody: {
    display: "flex",
    justifyContent: "center",
    padding: 40,
  },
  badgesRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },
  badge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 10px",
  },
  tierBadge: {
    fontSize: 12,
    fontWeight: 500,
    padding: "4px 10px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  value: {
    fontSize: 14,
    color: "#111827",
  },
  clickable: {
    cursor: "pointer",
    color: "#3b82f6",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  linkIcon: {
    fontSize: 10,
  },
  referrerRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  noValue: {
    fontSize: 14,
    color: "#111827",
  },
  changeLink: {
    fontSize: 12,
    color: "#3b82f6",
    cursor: "pointer",
    fontWeight: 500,
  },
  actionSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
};

export default AdminUserOverviewCard;
